import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import { useToast } from '../../hooks/useToast';
import { COLLECTIONS } from '../../constants/collections';
import { CONFIG_CATEGORIES } from '../../constants/systemConfig';
import { getAllDocuments, createDocument, updateDocument } from '../../services/firestore/firestoreService';

const getGeneralSettings = async (tenantId) => {
    const conditions = [
        { type: 'where', field: 'tenantId', operator: '==', value: tenantId },
        { type: 'where', field: 'category', operator: '==', value: CONFIG_CATEGORIES.GENERAL_SETTINGS }
    ];
    const results = await getAllDocuments(COLLECTIONS.SYSTEM_CONFIGS, conditions);
    return results.length > 0 ? results[0] : null;
};

const saveGeneralSettings = async (tenantId, settingsId, data) => {
    if (settingsId) {
        return await updateDocument(COLLECTIONS.SYSTEM_CONFIGS, settingsId, {
            ...data,
            updatedAt: new Date()
        });
    } else {
        const payload = {
            ...data,
            category: CONFIG_CATEGORIES.GENERAL_SETTINGS,
            tenantId,
            createdAt: new Date(),
            updatedAt: new Date(),
            isActive: true
        };
        return await createDocument(COLLECTIONS.SYSTEM_CONFIGS, payload);
    }
};

const CarrierPerformanceConfig = ({ user }) => {
    const { success, error: toastError } = useToast();
    const [loading, setLoading] = useState(false);
    const [existingId, setExistingId] = useState(null);
    const [settingsData, setSettingsData] = useState(null);

    const [formData, setFormData] = useState({
        onTimeWeight: 40,
        accuracyWeight: 30,
        revisionWeight: 20,
        completionWeight: 10,
        toleranceMinutes: 15
    });

    useEffect(() => {
        if (user?.tenantId) {
            loadSettings();
        }
    }, [user?.tenantId]);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const settings = await getGeneralSettings(user.tenantId);
            if (settings) {
                setExistingId(settings.id);
                setSettingsData(settings);
                setFormData({
                    onTimeWeight: settings.onTimeWeight || 40,
                    accuracyWeight: settings.accuracyWeight || 30,
                    revisionWeight: settings.revisionWeight || 20,
                    completionWeight: settings.completionWeight || 10,
                    toleranceMinutes: settings.toleranceMinutes || 15
                });
            }
        } catch (err) {
            console.error(err);
            toastError('Ayarlar yüklenemedi.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        const total = formData.onTimeWeight + formData.accuracyWeight + formData.revisionWeight + formData.completionWeight;

        if (total !== 100) {
            toastError('Hata: Toplam ağırlık %100 olmalıdır!');
            setLoading(false);
            return;
        }

        try {
            // Merge with existing settings to not lose other values
            const payload = { ...settingsData, ...formData };
            await saveGeneralSettings(user.tenantId, existingId, payload);
            success('Nakliyeci performans kriterleri güncellendi.');
            loadSettings();
        } catch (err) {
            console.error(err);
            toastError('Kaydedilemedi: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const totalWeight = formData.onTimeWeight + formData.accuracyWeight + formData.revisionWeight + formData.completionWeight;

    return (
        <div>
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h3 className="mb-xs">Nakliyeci Performans Kriterleri</h3>
                    <p className="text-secondary text-sm">
                        Nakliyeci karnesi ve pivot analizinde kullanılan puanlama ağırlıklarını buradan yönetebilirsiniz.
                    </p>
                </div>
                <Button variant="primary" onClick={handleSave} loading={loading} style={{ minWidth: '120px' }}>Kaydet</Button>
            </div>

            <div className="card-premium glow-theme p-xl bg-glass">
                <div className="p-md bg-blue-500/5 border border-blue-500/20 rounded-xl mb-xl">
                    <p className="text-sm text-secondary leading-relaxed">
                        ✨ <strong>Nasıl Çalışır?</strong> Sistemde her nakliyeci için 10 üzerinden bir "Genel Skor" hesaplanır. Bu skorun hangi metriğe ne kadar bağlı olacağını aşağıdaki yüzdelerle belirlersiniz.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-xl">
                    {/* Weight Inputs */}
                    <div className="space-y-lg">
                        <div className="form-group">
                            <label className="form-label text-xs font-black tracking-widest uppercase opacity-60">Zamanında Varış Ağırlığı (%)</label>
                            <input
                                type="number"
                                className="glass-input w-full"
                                value={formData.onTimeWeight}
                                onChange={e => setFormData({ ...formData, onTimeWeight: parseInt(e.target.value) || 0 })}
                            />
                            <small className="text-secondary mt-1 block">Araçların randevu saatine uyumunun skora etkisi.</small>
                        </div>

                        <div className="form-group">
                            <label className="form-label text-xs font-black tracking-widest uppercase opacity-60">Veri Doğruluğu Ağırlığı (%)</label>
                            <input
                                type="number"
                                className="glass-input w-full"
                                value={formData.accuracyWeight}
                                onChange={e => setFormData({ ...formData, accuracyWeight: parseInt(e.target.value) || 0 })}
                            />
                            <small className="text-secondary mt-1 block">Plaka ve sürücü bilgilerinin doğruluğunun skora etkisi.</small>
                        </div>

                        <div className="form-group">
                            <label className="form-label text-xs font-black tracking-widest uppercase opacity-60">Revizyon Sadakati Ağırlığı (%)</label>
                            <input
                                type="number"
                                className="glass-input w-full"
                                value={formData.revisionWeight}
                                onChange={e => setFormData({ ...formData, revisionWeight: parseInt(e.target.value) || 0 })}
                            />
                            <small className="text-secondary mt-1 block">Rezervasyon sonrası yapılan değişikliklerin (negatif) etkisi.</small>
                        </div>

                        <div className="form-group">
                            <label className="form-label text-xs font-black tracking-widest uppercase opacity-60">İşlem Tamamlanma Ağırlığı (%)</label>
                            <input
                                type="number"
                                className="glass-input w-full"
                                value={formData.completionWeight}
                                onChange={e => setFormData({ ...formData, completionWeight: parseInt(e.target.value) || 0 })}
                            />
                            <small className="text-secondary mt-1 block">Seferin sorunsuz tamamlanma oranının etkisi.</small>
                        </div>
                    </div>

                    {/* Tolerance & Summary Panel */}
                    <div className="flex flex-col gap-lg">
                        <div className="form-group p-lg bg-white/5 rounded-2xl border border-white/5">
                            <label className="form-label text-xs font-black tracking-widest uppercase opacity-60">Zamanında Varış Toleransı (Dakika)</label>
                            <div className="flex items-center gap-md mt-sm">
                                <input
                                    type="number"
                                    className="glass-input flex-1"
                                    value={formData.toleranceMinutes}
                                    onChange={e => setFormData({ ...formData, toleranceMinutes: parseInt(e.target.value) || 0 })}
                                />
                                <span className="text-secondary font-bold">DK</span>
                            </div>
                            <small className="text-secondary mt-3 block leading-relaxed italic">
                                ℹ️ Örn: 15 dakika ayarlanırsa, randevusu 10:00 olan bir araç 10:15'e kadar gelirse "Zamanında" kabul edilir.
                            </small>
                        </div>

                        <div className={`mt-auto p-xl rounded-2xl border transition-smooth ${totalWeight === 100 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                            <div className="flex justify-between items-center">
                                <span className="font-black uppercase tracking-widest text-xs opacity-60">Toplam Ağırlık</span>
                                <span className={`text-3xl font-black ${totalWeight === 100 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    %{totalWeight}
                                </span>
                            </div>
                            {totalWeight !== 100 && (
                                <p className="text-[10px] text-red-400 font-bold uppercase tracking-widest mt-md animate-pulse text-center">
                                    ⚠️ Skor hesaplanabilmesi için toplam %100 olmalıdır!
                                </p>
                            )}
                            {totalWeight === 100 && (
                                <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest mt-md text-center">
                                    ✅ Kriterler dengeli ve geçerli.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CarrierPerformanceConfig;
